import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  Alert,
  CircularProgress,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import {
  Send,
  Close,
} from '@mui/icons-material';
import * as Actions from 'actions';

const SendNotificationDialog = (props) => {
  const {
    open,
    onClose,
    selectedContacts = [],
    contactList = null, // For sending to a specific list
    sendNotificationToContacts,
    sendNotificationToList,
    notificationResult,
    isLoading,
    reqStatus,
    contactLists = { lists: [] },
    getContactLists,
    messageTemplates = { templates: [], categories: {}, total: 0 },
    getMessageTemplates,
  } = props;

  const [campaign, setCampaign] = useState('');
  const [params, setParams] = useState('');
  const [media, setMedia] = useState('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [sendMode, setSendMode] = useState(contactList ? 'list' : 'contacts'); // 'contacts' or 'list'
  const [selectedListId, setSelectedListId] = useState(contactList ? contactList._id : '');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [templateParams, setTemplateParams] = useState({});

  // Load contact lists and templates when dialog opens
  useEffect(() => {
    if (open) {
      if (!contactList) {
        getContactLists({});
      }
      // Always load templates
      if (messageTemplates.templates.length === 0) {
        getMessageTemplates();
      }
    }
  }, [open, getContactLists, getMessageTemplates, contactList, messageTemplates.templates.length]);

  useEffect(() => {
    if (
      reqStatus === 'SELLER_SEND_NOTIFICATION_TO_CONTACTS_SUCCESS' ||
      reqStatus === 'SELLER_SEND_NOTIFICATION_TO_LIST_SUCCESS'
    ) {
      setShowSuccess(true);
      // Auto close after 2 seconds
      setTimeout(() => {
        handleClose();
      }, 2000);
    }
  }, [reqStatus]);

  // Set initial mode based on props
  useEffect(() => {
    if (contactList) {
      setSendMode('list');
      setSelectedListId(contactList._id);
    } else if (selectedContacts.length > 0) {
      setSendMode('contacts');
    }
  }, [contactList, selectedContacts]);

  const handleClose = () => {
    setCampaign('');
    setParams('');
    setMedia('');
    setShowSuccess(false);
    setSendMode(contactList ? 'list' : 'contacts');
    setSelectedListId(contactList ? contactList._id : '');
    setSelectedTemplate(null);
    setTemplateParams({});
    onClose();
  };

  const handleTemplateChange = (event) => {
    const templateId = event.target.value;
    const template = messageTemplates.templates.find(t => t.id === templateId);
    
    if (template) {
      setSelectedTemplate(template);
      setCampaign(template.campaign);
      
      // Initialize template parameters
      const initialParams = {};
      if (template.parameters) {
        template.parameters.forEach(param => {
          initialParams[param.name] = '';
        });
      }
      setTemplateParams(initialParams);
      setParams(''); // Clear manual params
    } else {
      setSelectedTemplate(null);
      setTemplateParams({});
      setCampaign('');
    }
  };

  const handleTemplateParamChange = (paramName, value) => {
    setTemplateParams(prev => ({
      ...prev,
      [paramName]: value
    }));
  };

  const buildParamsString = () => {
    if (selectedTemplate && selectedTemplate.parameters) {
      // Build comma-separated params in order
      return selectedTemplate.parameters
        .map(param => templateParams[param.name] || '')
        .join(', ');
    }
    return params;
  };

  const handleSend = () => {
    if (!campaign.trim()) {
      alert('Please select a message template or enter a campaign name');
      return;
    }

    // Validate required template parameters
    if (selectedTemplate && selectedTemplate.parameters) {
      const requiredParams = selectedTemplate.parameters.filter(p => p.required);
      const missingParams = requiredParams.filter(p => !templateParams[p.name] || !templateParams[p.name].trim());
      
      if (missingParams.length > 0) {
        alert(`Please fill in required parameter: ${missingParams[0].label}`);
        return;
      }
    }

    const paramsString = buildParamsString();

    if (sendMode === 'contacts') {
      if (selectedContacts.length === 0) {
        alert('Please select at least one contact');
        return;
      }

      const contactIds = selectedContacts.map(contact => contact._id);

      sendNotificationToContacts({
        contact_ids: contactIds,
        campaign: campaign.trim(),
        params: paramsString.trim(),
        media: media.trim(),
      });
    } else {
      // Send to list
      if (!selectedListId) {
        alert('Please select a contact list');
        return;
      }

      sendNotificationToList({
        list_id: selectedListId,
        campaign: campaign.trim(),
        params: paramsString.trim(),
        media: media.trim(),
      });
    }
  };

  const getRecipientCount = () => {
    if (sendMode === 'contacts') {
      return selectedContacts.length;
    } else {
      const list = contactLists.lists.find(l => l._id === selectedListId);
      return list ? list.contact_count || 0 : 0;
    }
  };

  const getRecipientDescription = () => {
    if (sendMode === 'contacts') {
      return `${selectedContacts.length} selected contact${selectedContacts.length !== 1 ? 's' : ''}`;
    } else {
      const list = contactLists.lists.find(l => l._id === selectedListId);
      if (list) {
        return `"${list.name}" list (${list.contact_count || 0} contacts)`;
      }
      return 'Select a list';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">Send Notification</Typography>
          <Button
            onClick={handleClose}
            color="inherit"
            size="small"
          >
            <Close />
          </Button>
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {!contactList && (
          <Box sx={{ mb: 3 }}>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Send notification to:
            </Typography>
            <RadioGroup
              value={sendMode}
              onChange={(e) => setSendMode(e.target.value)}
              row
            >
              <FormControlLabel
                value="contacts"
                control={<Radio />}
                label="Selected Contacts"
                disabled={selectedContacts.length === 0}
              />
              <FormControlLabel
                value="list"
                control={<Radio />}
                label="Contact List"
              />
            </RadioGroup>
          </Box>
        )}

        <Box sx={{ mb: 2 }}>
          {sendMode === 'contacts' ? (
            <>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                {getRecipientDescription()}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {selectedContacts.slice(0, 5).map((contact) => (
                  <Chip
                    key={contact._id}
                    label={contact.name}
                    size="small"
                    variant="outlined"
                  />
                ))}
                {selectedContacts.length > 5 && (
                  <Chip
                    label={`+${selectedContacts.length - 5} more`}
                    size="small"
                    variant="outlined"
                  />
                )}
              </Box>
            </>
          ) : (
            <>
              <FormControl fullWidth margin="normal">
                <InputLabel>Select Contact List</InputLabel>
                <Select
                  value={selectedListId}
                  onChange={(e) => setSelectedListId(e.target.value)}
                  label="Select Contact List"
                  disabled={!!contactList}
                >
                  {contactLists.lists.map((list) => (
                    <MenuItem key={list._id} value={list._id}>
                      {list.name} ({list.contact_count || 0} contacts)
                      {list.list_type === 'default' && ' - Default'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {selectedListId && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {getRecipientDescription()}
                </Typography>
              )}
            </>
          )}
        </Box>

        {showSuccess && notificationResult && (
          <Alert severity="success" sx={{ mb: 2 }}>
            Notifications sent: {notificationResult.sent}/{notificationResult.total}
            {notificationResult.failed > 0 && ` (${notificationResult.failed} failed)`}
          </Alert>
        )}

        {/* Template Selector */}
        <FormControl fullWidth margin="normal">
          <InputLabel>Message Template</InputLabel>
          <Select
            value={selectedTemplate ? selectedTemplate.id : ''}
            onChange={handleTemplateChange}
            label="Message Template"
          >
            <MenuItem value="">
              <em>Select a template...</em>
            </MenuItem>
            {Object.keys(messageTemplates.categories).map(category => [
              <MenuItem key={`category-${category}`} disabled sx={{ fontWeight: 'bold', fontSize: '0.875rem' }}>
                {category}
              </MenuItem>,
              ...messageTemplates.categories[category].map(template => (
                <MenuItem key={template.id} value={template.id} sx={{ pl: 4 }}>
                  {template.name}
                </MenuItem>
              ))
            ])}
          </Select>
        </FormControl>

        {/* Template Details */}
        {selectedTemplate && (
          <Box sx={{ mb: 2, mt: 1 }}>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>{selectedTemplate.name}</strong>: {selectedTemplate.description}
              </Typography>
              {selectedTemplate.example && (
                <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                  Example: {selectedTemplate.example}
                </Typography>
              )}
            </Alert>

            {/* Template Parameters */}
            {selectedTemplate.parameters && selectedTemplate.parameters.map(param => (
              <TextField
                key={param.name}
                fullWidth
                label={param.label}
                placeholder={param.placeholder}
                value={templateParams[param.name] || ''}
                onChange={(e) => handleTemplateParamChange(param.name, e.target.value)}
                margin="normal"
                required={param.required}
                multiline={param.multiline}
                rows={param.multiline ? 3 : 1}
                helperText={param.required ? 'Required' : 'Optional'}
              />
            ))}
          </Box>
        )}

        {/* Manual Campaign Entry (if no template selected) */}
        {!selectedTemplate && (
          <>
            <TextField
              fullWidth
              label="Campaign Name"
              placeholder="e.g., welcome_message, offer_alert"
              value={campaign}
              onChange={(e) => setCampaign(e.target.value)}
              margin="normal"
              required
              helperText="WhatsApp campaign template name configured in your system"
            />

            <TextField
              fullWidth
              label="Campaign Parameters"
              placeholder="e.g., John Doe, 20% OFF"
              value={params}
              onChange={(e) => setParams(e.target.value)}
              margin="normal"
              multiline
              rows={2}
              helperText="Comma-separated parameters for the template (optional)"
            />
          </>
        )}

        {/* Media URL - only show if template supports it or no template selected */}
        {(!selectedTemplate || selectedTemplate.supportsMedia) && (
          <TextField
            fullWidth
            label="Media URL"
            placeholder="https://example.com/image.jpg"
            value={media}
            onChange={(e) => setMedia(e.target.value)}
            margin="normal"
            helperText="URL of image/video to send with message (optional)"
          />
        )}

        <Box sx={{ mt: 2 }}>
          <Alert severity="info">
            <Typography variant="body2">
              {selectedTemplate ? (
                <>
                  <strong>Ready to send:</strong> Your message will be sent using the "{selectedTemplate.name}" template.
                  {selectedTemplate.parameters && selectedTemplate.parameters.length > 0 && (
                    <> Fill in all required parameters above before sending.</>
                  )}
                </>
              ) : (
                <>
                  <strong>Note:</strong> Messages will be sent via WhatsApp using your configured campaign templates.
                  Select a template above or enter a campaign name manually.
                </>
              )}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSend}
          disabled={isLoading || !campaign.trim()}
          startIcon={isLoading ? <CircularProgress size={20} /> : <Send />}
        >
          {isLoading ? 'Sending...' : 'Send Notification'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const _SendNotificationDialog = connect(
  ({ sellerContacts }) => ({
    notificationResult: sellerContacts.notificationResult,
    isLoading: sellerContacts.isLoading,
    reqStatus: sellerContacts.reqStatus,
    contactLists: sellerContacts.contactLists,
    messageTemplates: sellerContacts.messageTemplates,
  }),
  (dispatch) =>
    bindActionCreators(
      {
        sendNotificationToContacts: Actions.sendNotificationToContacts,
        sendNotificationToList: Actions.sendNotificationToList,
        getContactLists: Actions.getContactLists,
        getMessageTemplates: Actions.getMessageTemplates,
      },
      dispatch
    )
)(SendNotificationDialog);

export default _SendNotificationDialog;

